/** Mosoo-only deck execution adapter. */

import type { SSEEvent } from '../types';
import { apiFetch } from './auth-fetch';
import type { ChatAttachment } from './client';

const API = '/api';

function nextRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function postMosooEvents(
  deckId: string,
  body: Record<string, unknown>,
): Promise<{ run_id: string | null; thread_id: string }> {
  const res = await apiFetch(`${API}/decks/${deckId}/mosoo-thread/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const responseBody = await res.json().catch(() => null) as {
    error?: { code?: string; message?: string };
    run_id?: string | null;
    thread_id?: string;
  } | null;
  if (!res.ok) {
    throw new Error(responseBody?.error?.message || `Mosoo send failed (${res.status})`);
  }
  if (!responseBody?.thread_id) throw new Error('Mosoo send did not return a Thread ID.');
  return {
    run_id: responseBody.run_id ?? null,
    thread_id: responseBody.thread_id,
  };
}

export async function sendMessageOnly(
  deckId: string,
  message: string,
  _attachments?: string[],
  opts?: { imageAttachments?: ChatAttachment[] },
): Promise<{ ok: boolean; message_id: string; turn_id: string; queued?: boolean }> {
  const requestId = nextRequestId();
  const mosoo = await postMosooEvents(deckId, {
    attachments: opts?.imageAttachments,
    events: [{ clientRequestId: requestId, text: message, type: 'user_message' }],
  });
  return {
    ok: true,
    message_id: requestId,
    turn_id: mosoo.run_id ?? mosoo.thread_id,
    queued: false,
  };
}

export async function interruptDeckTurn(
  deckId: string,
  _reason = 'user_barge_in',
): Promise<{ ok: boolean; interrupted: boolean; turn_id?: string; unsupported?: boolean }> {
  const mosoo = await postMosooEvents(deckId, {
    events: [{ type: 'user_interrupt' }],
  });
  return {
    ok: true,
    interrupted: true,
    turn_id: mosoo.run_id ?? undefined,
  };
}

export type EventChannel = 'chat' | 'activity' | 'meta';

const ACTIVITY_TYPES = new Set([
  'activity',
  'tool_use',
  'tool_result',
  'thinking',
  'agent_thinking',
  'message_delta',
]);
const META_TYPES = new Set(['connected', 'disconnected', 'warning']);

export function classifyEvent(event: SSEEvent): EventChannel {
  const type = event.type;
  if (META_TYPES.has(type)) return 'meta';
  if (ACTIVITY_TYPES.has(type)) return 'activity';
  return 'chat';
}

export function parseSSELines(text: string): SSEEvent[] {
  const events: SSEEvent[] = [];
  const lines = text.split('\n');
  for (const line of lines) {
    if (!line.startsWith('data: ')) continue;
    try {
      events.push(JSON.parse(line.slice(6)));
    } catch {
      // skip malformed
    }
  }
  return events;
}
